import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, OnInit } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { map, takeUntil } from 'rxjs';
import { MatCheckbox } from '@angular/material/checkbox';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import type { Entry, Module } from '../../../features/admin/modules/modules.model';
import { UserRolesStore } from '../../../features/admin/user-roles/user-roles.store';
import { UtilityService } from '../../services/utility.service';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { UserStore } from '../../../core/services/user.store';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { commitUserValue } from './user-edit.util';

@Component({
  selector: 'app-userroles-checkboxes',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="mb-4">
      @if (props.label) {
        <label class="font-medium inline-block mb-2">
          {{ props.label }} {{ field.props.required ? '*' : '' }}
          <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
        </label>
      }
      <div class="block">
        @for (userRole of userRoles(); track userRole.id) {
          <mat-checkbox
            class="block"
            [formlyDataCy]="field"
            (ngModelChange)="updateFormControlValue($event, userRole.id)"
            [(ngModel)]="checked[userRole.id]"
            [disabled]="props.disabled || ownAccount()">
            {{ userRole.name }}
          </mat-checkbox>
        } @empty {
          @switch (emptyStateReason()) {
            @case ('onboarding') {
              <p class="text-sm text-warn-500">
                Deze gebruiker is nog in onboarding. De onboarding moet eerst worden voltooid voordat rollen kunnen
                worden toegekend.
              </p>
            }
            @case ('no-category') {
              <p class="text-sm text-warn-500">Bedrijf heeft geen categorie — kan rollen niet filteren.</p>
            }
          }
        }
      </div>

      @if (field.props.description) {
        <mat-hint>{{ field.props.description }}</mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  imports: [
    FormlyModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    InfoTooltipComponent,
    FormlyModule,
    FormsModule,
    MatCheckbox,
    FormlyDataCyDirective,
    FormlyErrorComponent,
  ],
})
export class UserRolesCheckboxComponent extends FieldType implements OnInit {
  checked = {};
  type = signal(null);
  company = signal<{ id?: string; selectedInput?: string }>(null);
  companyEntry = signal<Entry>(null);
  ownAccount = signal(false);
  /**
   * True buiten de gebruikers-context: het host-formulier heeft geen
   * `general`/`settings`-groepen (bv. "Geldt voor rollen" op
   * document-templates). Dan is er geen type- of bedrijfscategorie-filter —
   * alle rollen zijn kiesbaar; de per-gebruiker-matching gebeurt runtime.
   */
  standalone = signal(false);
  modulesStore = inject(ModulesStore);
  userRolesStore = inject(UserRolesStore);
  userStore = inject(UserStore);
  entriesService = inject(EntriesService);
  moduleId: Module['id'];
  parentForm;
  path: 'general' | 'settings';
  userRolesOfCurrentType = computed(() => {
    if (this.standalone()) {
      return this.userRolesStore.userRoles();
    }
    if (this.type() === 'klant-medewerker') {
      return this.userRolesStore.clientUserRoles();
    }
    if (this.type() === 'medewerker') {
      return this.userRolesStore.employeeUserRoles();
    }
    if (this.type() === 'onboarding') {
      return this.userRolesStore.onboardingUserRoles();
    }
    return [];
  });
  userRoles = computed(() => {
    if (this.standalone()) {
      return this.userRolesOfCurrentType();
    }
    const currentCompanyTypes =
      (this.companyEntry()?.entry?.general?.categories ?? [
        UtilityService.getValueOfRootForm(this.field)?.settings?.['company-type'],
      ]) ||
      [];

    return this.userRolesOfCurrentType().filter(userRole => {
      return currentCompanyTypes?.some((category: Entry) => {
        return userRole.companyCategories.includes(category?.id);
      });
    });
  });
  emptyStateReason = computed<'onboarding' | 'no-category' | null>(() => {
    if (this.standalone() || this.userRoles().length > 0) {
      return null;
    }
    const assignedRoleIds: string[] = Array.isArray(this.formControl?.value) ? this.formControl.value : [];
    const onboardingRoleIds = new Set(this.userRolesStore.onboardingUserRoles().map(role => role.id));
    if (assignedRoleIds.some(id => onboardingRoleIds.has(id))) {
      return 'onboarding';
    }
    const categories = this.companyEntry()?.entry?.general?.categories;
    if (!categories || categories.length === 0) {
      return 'no-category';
    }
    return null;
  });
  private destroy$ = injectDestroy();
  /** Flipped at the end of ngOnInit so the roles-load effect only acts once state is set up. */
  private readonly initialized = signal(false);

  constructor() {
    super();
    // On a cold start the UserRolesStore can still be empty when this field inits, so the
    // initial setCheckboxes() marks nothing — and a subsequent click would then rebuild the
    // form value from an empty `checked` map, dropping the saved role ids. Re-apply the saved
    // selection whenever the store (re)loads. setCheckboxes is additive/idempotent, so extra
    // runs are harmless.
    effect(() => {
      this.userRolesStore.userRoles(); // track: re-run when roles arrive
      if (!this.initialized()) return;
      this.setCheckboxes();
    });
  }

  ngOnInit() {
    if (!Array.isArray(this.formControl.value)) {
      this.field.formControl.setValue([]);
    }

    if (this.userStore.currentUserId() === this.options.formState.entry?.id) {
      this.ownAccount.set(true);
    }

    const rootValue = UtilityService.getValueOfRootForm(this.field);
    this.standalone.set(!rootValue?.general && !rootValue?.settings);
    if (this.standalone()) {
      // Geen gebruikers-formulier: niets om op type/bedrijf te volgen.
      this.setCheckboxes();
      this.initialized.set(true);
      return;
    }

    // TODO make an more dynamisc way to get the type, search for the field that has options 'medewerker' or 'klant-medewerker'
    this.type.set(rootValue?.general?.type ?? rootValue?.settings?.type);
    const initialCompany = rootValue?.general?.company ?? null;
    this.company.set(initialCompany);
    this.fetchCompanyEntry(initialCompany?.id);

    this.path = rootValue?.general?.type ? 'general' : 'settings';
    this.parentForm = UtilityService.getFormOfRootForm(this.field);

    // getRawValue(), not the emitted value: `valueChanges` emits `form.value`, which leaves out
    // DISABLED controls. The users form disables "Type gebruiker" for some admin roles
    // (`disableUserRole` on that field), and with the emitted value the first change on any other
    // field read the type as undefined, emptied `userRolesOfCurrentType()` and rendered an empty
    // roles block for a new user (measured on distrodot-dev-api-test, 2026-10-07). The raw value
    // keeps the disabled, pre-filled type. The init path above already reads the raw value.
    // No distinctUntilChanged: getRawValue() is a new object on every emission, and the subscriber
    // already compares type and company itself.
    this.parentForm.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        map(() => this.parentForm.formControl.getRawValue())
      )
      .subscribe(value => {
      if (this.type() != value[this.path]?.type) {
        this.type.set(value[this.path].type);
        this.checked = {};
        this.setCheckboxes();
      }
      if (this.company()?.id != value.general?.company?.id) {
        this.company.set(value.general.company);
        this.fetchCompanyEntry(value.general?.company?.id);
      }
    });

    this.setCheckboxes();
    this.initialized.set(true);
  }

  fetchCompanyEntry(companyId: string) {
    if (!companyId) {
      this.companyEntry.set(null);
      return;
    }
    const companiesModuleId = this.modulesStore.moduleCodeToId('companies');
    if (!companiesModuleId) {
      return;
    }
    this.entriesService
      .getEntry(companiesModuleId, companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(entry => {
        this.companyEntry.set(entry);
      });
  }

  setCheckboxes() {
    this.formControl.value?.forEach(value => {
      if (this.standalone()) {
        if (this.userRolesStore.userRoles().find(role => role.id === value)) {
          this.checked[value] = true;
        }
        return;
      }
      const isEmployeeRole = this.userRolesStore.employeeUserRoles().find(role => role.id === value);
      const isClientRole = this.userRolesStore.clientUserRoles().find(role => role.id === value);
      const isOnboardingRole = this.userRolesStore.onboardingUserRoles().find(role => role.id === value);
      if (isEmployeeRole || isClientRole || isOnboardingRole) {
        this.checked[value] = true;
      }
    });
  }

  updateFormControlValue(checked: boolean, userRoleId: string) {
    if (!Array.isArray(this.field.formControl.value)) {
      this.field.formControl.setValue([]);
    }

    this.checked[userRoleId] = checked;
    const userRoles = [];

    Object.keys(this.checked).forEach(key => {
      if (this.checked[key] === true) {
        userRoles.push(key);
      }
    });

    commitUserValue(this.field.formControl, userRoles);
  }
}
